import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { Pill, RefreshCw, Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import EmptyState from "@/components/EmptyState";
import MedicationCard from "@/components/MedicationCard";
import { useToast } from "@/hooks/use-toast";

export default function MedicationsPanel() {
  const { user } = useAuth();
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [bufferDays, setBufferDays] = useState(5);
  const [anchors, setAnchors] = useState({});
  const { toast } = useToast();

  const fetchMedications = (showSync = false) => {
    if (showSync) setSyncing(true);
    API.get("/medications").then(({ data }) => { setMedications(data || []); setLoading(false); setSyncing(false); }).catch(() => { setLoading(false); setSyncing(false); });
  };

  const fetchAnchors = useCallback(() => {
    API.get("/medication-reminders/anchors").then(({ data }) => {
      const map = {};
      (data || []).forEach(a => { map[a.med_key] = a; });
      setAnchors(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchMedications();
    fetchAnchors();
    API.get("/medication-reminders/settings").then(({ data }) => {
      setRemindersEnabled(!!data.enabled);
      setBufferDays(data.buffer_days ?? 5);
    }).catch(() => {});
  }, [user, fetchAnchors]);

  const toggleReminders = async (next) => {
    setRemindersEnabled(next);
    try {
      await API.put("/medication-reminders/settings", { enabled: next });
      toast({ title: next ? "Refill reminders on" : "Refill reminders off", description: next ? `You'll be reminded ~${bufferDays} days before a med runs out.` : "You won't receive refill reminders.", duration: 3500 });
    } catch {
      setRemindersEnabled(!next);
      toast({ title: "Couldn't update setting", variant: "destructive" });
    }
  };

  const saveAnchor = async (medication_name, last_filled_date, days_supply) => {
    const { data } = await API.put("/medication-reminders/anchors", { medication_name, last_filled_date, days_supply });
    setAnchors(prev => ({ ...prev, [data.med_key]: data }));
    toast({ title: "Refill reminder set", description: `We'll remind you ~${bufferDays} days before ${medication_name} runs out.`, duration: 3500 });
  };

  const clearAnchor = async (med_key) => {
    await API.delete(`/medication-reminders/anchors/${encodeURIComponent(med_key)}`);
    setAnchors(prev => {
      const next = { ...prev };
      delete next[med_key];
      return next;
    });
    toast({ title: "Reminder cleared", duration: 2500 });
  };

  const hasIntakeq = medications.some(m => m.source === "intakeq");
  const fromNote = medications.some(m => m.source === "intakeq" && m.source_detail === "provider_note");
  const medKey = (name) => (name || "").replace(/\([^)]*\)/g, "").replace(/\s+/g, " ").trim().toLowerCase();

  if (loading) return <div className="py-12 flex items-center justify-center"><div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="medications-page">
      <div className="flex items-center justify-between gap-2 mb-3">
        {hasIntakeq ? (
          <div className="bg-sage/15 border border-sage/40 rounded-lg px-2.5 py-1.5 flex items-center gap-2 min-w-0">
            <div className="w-2 h-2 rounded-full bg-sage shrink-0" />
            <p className="text-xs font-medium truncate">
              {fromNote ? "Synced from your most recent provider note" : "Synced from your IntakeQ intake forms"}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Your current medications and dosing</p>
        )}
        <Button variant="ghost" size="sm" onClick={() => fetchMedications(true)} disabled={syncing} className="gap-1.5 shrink-0" data-testid="sync-medications-button">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <div className="bg-card border border-border rounded-xl p-3 mb-4 flex items-center justify-between gap-3" data-testid="refill-reminder-settings">
        <div className="flex items-center gap-2 min-w-0">
          {remindersEnabled ? <Bell className="w-4 h-4 text-primary shrink-0" /> : <BellOff className="w-4 h-4 text-muted-foreground shrink-0" />}
          <div className="min-w-0">
            <p className="text-sm font-medium leading-tight">Refill reminders</p>
            <p className="text-[11px] text-muted-foreground leading-tight">{remindersEnabled ? `We'll remind you ~${bufferDays} days before a med runs out` : "You won't receive refill reminders"}</p>
          </div>
        </div>
        <Switch checked={remindersEnabled} onCheckedChange={toggleReminders} data-testid="refill-reminder-toggle" />
      </div>

      {medications.length === 0 ? (
        <EmptyState icon={Pill} title="No medications found" description="Your medications will appear here once they're recorded in your intake forms or added by your provider" />
      ) : (
        <div className="space-y-4">
          {medications.map(med => (
            <MedicationCard
              key={med.id}
              medication={med}
              anchor={anchors[medKey(med.name)]}
              onSaveAnchor={saveAnchor}
              onClearAnchor={clearAnchor}
              remindersEnabled={remindersEnabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}
